"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildCanonicalQuoteListPrice = exports.computeCanonicalQuotePricing = void 0;
const roundCurrency = (value) => Math.round(value * 100) / 100;
const toNumber = (value) => {
    if (value == null)
        return null;
    if (typeof value === "number")
        return Number.isFinite(value) ? value : null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};
const getPlan = (treatmentPlan) => {
    if (!treatmentPlan || Array.isArray(treatmentPlan))
        return null;
    return treatmentPlan;
};
const getCards = (pricingSummary) => (pricingSummary?.cards ?? []).filter((card) => Boolean(card));
const resolvePreferredCard = (cards, pricingSummary) => {
    if (cards.length === 0)
        return null;
    const acceptedKey = pricingSummary?.acceptedPlanKey ?? null;
    if (acceptedKey) {
        const accepted = cards.find((card) => card.key === acceptedKey);
        if (accepted)
            return accepted;
    }
    const recommendedKey = pricingSummary?.recommendedCardKey ?? null;
    if (recommendedKey) {
        const recommended = cards.find((card) => card.key === recommendedKey);
        if (recommended)
            return recommended;
    }
    return cards.find((card) => card.isRecommended) ?? cards[0] ?? null;
};
const resolveProcedureFallback = (pricingSummary) => {
    const cards = getCards(pricingSummary);
    const preferred = resolvePreferredCard(cards, pricingSummary);
    if (preferred)
        return toNumber(preferred.total) ?? 0;
    const flatAmount = toNumber(pricingSummary?.procedures?.flatAmount);
    return flatAmount ?? 0;
};
const computeCanonicalQuotePricing = (input) => {
    const plan = getPlan(input.treatmentPlan);
    const pricingSummary = plan?.pricingSummary ?? null;
    const cards = getCards(pricingSummary);
    const preferredCard = resolvePreferredCard(cards, pricingSummary);
    const feeBreakdown = pricingSummary?.fees;
    const organizationFeeIncludedInPatientTotal = feeBreakdown?.organization?.includedInPatientTotal ?? true;
    const organizationFee = toNumber(input.organizationFee) ??
        toNumber(feeBreakdown?.organization?.amount) ??
        0;
    const logisticsTotal = toNumber(input.dailyFeesTotal) ??
        toNumber(feeBreakdown?.travelDailyFees?.totals?.total) ??
        0;
    const taxes = toNumber(input.taxes) ?? 0;
    const procedureSubtotal = toNumber(input.procedureSubtotal) ??
        toNumber(preferredCard?.total) ??
        resolveProcedureFallback(pricingSummary);
    const organizationFeePatientAmount = organizationFeeIncludedInPatientTotal ? organizationFee : 0;
    const computedSubtotal = roundCurrency(procedureSubtotal + logisticsTotal + organizationFeePatientAmount);
    const subtotal = toNumber(input.subtotal) ?? computedSubtotal;
    const computedTotal = roundCurrency(subtotal + taxes);
    const total = toNumber(input.total) ?? computedTotal;
    const patientTotalsByCard = cards
        .map((card) => {
        const procedureTotal = toNumber(card.total) ?? 0;
        return {
            key: card.key,
            procedureTotal,
            total: roundCurrency(procedureTotal + logisticsTotal + organizationFeePatientAmount + taxes),
            currency: card.currency ?? input.currency ?? "USD",
        };
    })
        .filter((entry) => entry.procedureTotal > 0);
    let patientRange = null;
    if (patientTotalsByCard.length > 1) {
        const totals = patientTotalsByCard.map((entry) => entry.total);
        patientRange = { min: Math.min(...totals), max: Math.max(...totals) };
    }
    return {
        currency: input.currency ?? "USD",
        acceptedPlanKey: preferredCard?.key ?? null,
        hasTieredPricing: patientTotalsByCard.length > 1,
        procedureSubtotal,
        organizationFee,
        organizationFeeIncludedInPatientTotal,
        organizationFeePatientAmount,
        logisticsTotal,
        subtotal,
        taxes,
        total,
        patientTotalsByCard,
        patientRange,
    };
};
exports.computeCanonicalQuotePricing = computeCanonicalQuotePricing;
const buildCanonicalQuoteListPrice = (input) => {
    const canonical = (0, exports.computeCanonicalQuotePricing)(input);
    const status = input.status ?? null;
    const acceptedLikeStatus = status === "accepted";
    if (acceptedLikeStatus) {
        return {
            currency: canonical.currency,
            display: "single",
            amount: canonical.total,
        };
    }
    if (canonical.patientRange && canonical.patientRange.max > canonical.patientRange.min) {
        return {
            currency: canonical.currency,
            display: "range",
            minAmount: canonical.patientRange.min,
            maxAmount: canonical.patientRange.max,
        };
    }
    return {
        currency: canonical.currency,
        display: "single",
        amount: canonical.total,
    };
};
exports.buildCanonicalQuoteListPrice = buildCanonicalQuoteListPrice;
//# sourceMappingURL=quotePricingCanonical.js.map